import { DelinquentBannerProps } from './DelinquentBanner.decl';

import {
  Typography,
  Banner,
  BannerIcon,
  BannerHeading,
  ThemeProvider,
  getColor,
} from '@aircall/tractor-v2';
import { ExternalLink } from '@components/Link';
import { PERMISSIONS_ACTIONS, RESOURCE } from '@constants/permissions.constants';
import { useCompanyBillingDetails } from '@hooks/useCompanyBillingDetails/useCompanyBillingDetails';
import { usePermissions } from '@hooks/usePermissions';
import styled from '@xstyled/styled-components';
import { Trans, useTranslation } from 'react-i18next';

const ErrorExternalLink = styled(ExternalLink)`
  color: ${getColor('text-critical')};

  &:hover {
    color: ${getColor('text-interactive-critical-hover')};
  }

  &:active {
    color: ${getColor('text-interactive-critical-active')};
  }
`;

export function DelinquentBanner({ 'data-test': dataTest }: DelinquentBannerProps) {
  const { checkAccess } = usePermissions();

  const { t } = useTranslation();

  const isOwner = checkAccess(PERMISSIONS_ACTIONS.READ, RESOURCE.COMPANY_BILLING);

  const {
    companyBillingDetails: { isDelinquent },
  } = useCompanyBillingDetails();

  if (!isDelinquent) {
    return null;
  }

  const bannerText = isOwner ? (
    <Trans
      i18nKey='delinquent_banner.banner_text'
      components={[<Typography key={0} variant='bodySemiboldS' fontWeight={700} as='span' />]}
    />
  ) : (
    <Trans
      i18nKey='delinquent_banner.banner_text_admin'
      components={[
        <Typography key={0} variant='bodySemiboldS' fontWeight={700} as='span' />,
        <ErrorExternalLink
          key={1}
          href={t('delinquent_banner.banner_text_admin_link')}
          data-test='delinquent_banner.banner_text_admin_link'
          target='_blank'
          color='text-interactive-critical'
        />,
      ]}
    />
  );

  return (
    <ThemeProvider>
      <Banner variant='critical' data-test={dataTest}>
        <BannerIcon />
        <BannerHeading>{bannerText}</BannerHeading>
      </Banner>
    </ThemeProvider>
  );
}
